






































































































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { EditorOptions } from "@toast-ui/editor/types/editor";
import { debounce } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useDarkModeController from "@/hooks/useDarkModeController";
import { $t } from "@/lib/i18nHelpers";
import { getMediaThumbnailUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { PostFileData } from "@/types";
import BaseInput from "@/components/BaseInput";
import BaseSpinner from "@/components/BaseSpinner";
import BaseStepper from "@/components/BaseStepper";
import ContentFeedPreview from "@/components/ContentFeedPreview";
import ModalView from "@/components/ModalView";
import WysiwygEditor, { Emitter } from "@/components/WysiwygEditor";
import AllowCommentsToggle from "../../components/AllowCommentsToggle";
import PostFilesArea from "../../components/PostFilesArea";
import PostHashtagsSettings from "../../components/PostHashtagsSettings";
import PostPriceSettings from "../../components/PostPriceSettings";
import SafeForWorkToggle from "../../components/SafeForWorkToggle";
import SchedulePostSettings from "../../components/SchedulePostSettings";
import StepperButtons from "../../components/StepperButtons";
import useStepperData from "../../hooks/useStepperData";
import { FileUploadData, PostPriceData } from "../../types";

export default defineComponent({
  name: "TextPostCreateView",
  components: {
    AllowCommentsToggle,
    PostPriceSettings,
    WysiwygEditor,
    BaseInput,
    BaseStepper,
    PostFilesArea,
    ModalView,
    StepperButtons,
    SchedulePostSettings,
    BaseSpinner,
    PostHashtagsSettings,
    ContentFeedPreview,
    SafeForWorkToggle,
  },
  props: {
    show: { type: Boolean, default: false },
    loading: { type: Boolean, required: true },
    saving: { type: Boolean, default: false },
    postData: { type: Object as PropType<ContentSet | null> },
    fileList: { type: Array as PropType<PostFileData[]>, required: true },
    previewFiles: { type: Array as PropType<Array<ContentFile | FileUploadData>>, required: true },
    isDraft: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUserStrict, authorities } = useAuthUser();
    const { isDarkMode } = useDarkModeController();

    const editorOptions: Partial<EditorOptions> = {
      initialEditType: "wysiwyg",
      height: "100%",
      hideModeSwitch: true,
      frontMatter: true,
      placeholder: $t("Content"),
      theme: isDarkMode.value ? "dark" : "light",
    };

    const recurringLevel = ref<SubscriptionLevel | null>(null);
    const oneTimePrice = ref<PaymentPackage | null>(null);
    const fileLoading = ref(false);
    const values = ref({
      title: props.postData?.title ?? "",
      description: props.postData?.description ?? "",
    });

    const isPostOwner = computed(() => props.postData?.userId === authUserStrict.value.id);
    const requiredAuthorities = computed(() => {
      return isPostOwner.value
        ? ["updateMyContentPrice", "updateMyContentSubscriptionLevel"]
        : ["updateAnyContentPrice", "updateAnyContentSubscriptionLevel"];
    });

    const allowUpdatePrice = computed(() => checkAuthorities(authorities.value, requiredAuthorities.value));
    const allowManageReviews = computed(() => config.featureManageFeedReviews);
    const allowManageSchedule = computed(() => props.isDraft || !props.postData?.publishTime);
    const hashtagsAvailable = computed(() => config.featureHashtags);
    const showSecondStep = computed(
      () =>
        allowUpdatePrice.value ||
        hashtagsAvailable.value ||
        allowManageReviews.value ||
        allowManageSchedule.value ||
        config.featureNSFW
    );

    const stepsArray = computed(
      () => ["filesAndPrice", ...(showSecondStep.value ? ["settings"] : []), "preview"] as const
    );
    const { stepperElRef, currentStep, steps, getStepName, onCurrentStepChange } = useStepperData(stepsArray);

    const title = computed(() => (props.isDraft ? $t("New text post") : $t("Edit text post")));
    const filesToRender = computed(() => props.fileList.filter(file => file.public));

    const priceData = computed<PostPriceData>(() => ({
      subscriptionLevelId: recurringLevel.value?.id ?? "0",
      pricePackageId: oneTimePrice.value?.id ?? "0",
    }));

    const fileUrls = computed(() =>
      props.fileList
        .filter(file => !file.public)
        .reduce((acc, file) => {
          acc[file.id] = getMediaThumbnailUrl(file, file.url, "720p");
          return acc;
        }, {} as Record<string, string>)
    );

    const onPriceChanged = ({
      recurringLevel: updatedRecurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      recurringLevel.value = updatedRecurringLevel;
      oneTimePrice.value = oneTimePackage;
    };

    const uploadImageHandler = (eventEmitter: Emitter) => {
      eventEmitter.removeEventHandler("addImageBlobHook");
      eventEmitter.listen("addImageBlobHook", (blob: Blob | File, callback: Function) => {
        const addImageOkButton = document.querySelector(
          ".toastui-editor-popup.toastui-editor-popup-add-image button.toastui-editor-ok-button"
        );
        if (fileLoading.value) return;

        fileLoading.value = true;
        addImageOkButton?.classList.add("disabled");

        emit("addFiles", [blob], { unshift: false, publicAccess: false }, (file?: ContentFile) => {
          fileLoading.value = false;
          addImageOkButton?.classList.remove("disabled");
          if (!file) return;
          callback(getMediaThumbnailUrl(file, file.urlPart, "720p"));
        });
        return false;
      });
    };

    const checkUnusedFiles = () => {
      for (const [id, url] of Object.entries(fileUrls.value)) {
        if (!values.value.description.includes(url)) {
          emit("fileRemove", id);
        }
      }
    };

    const onPublish = () => {
      const data: Omit<InputContentSetUpdate, "id"> = {
        title: values.value.title,
        description: values.value.description,
        published: true,
        allowReviews: allowManageReviews.value ? props.postData?.allowReviews : undefined,
        tagIds: props.postData?.tagIds ?? [],
      };
      checkUnusedFiles();
      emit("update", { data, closeOnSave: true });
    };

    watch(
      values,
      debounce(() => {
        const data: Omit<InputContentSetUpdate, "id"> = {
          title: values.value.title,
          description: values.value.description,
        };
        checkUnusedFiles();
        emit("partialUpdate", { data });
      }, 500),
      { deep: true }
    );
    return {
      stepperElRef,
      values,
      title,
      currentStep,
      steps,
      hashtagsAvailable,
      allowManageReviews,
      allowManageSchedule,
      showSecondStep,
      ContentFeedTypeEnum,
      editorOptions,
      priceData,
      filesToRender,
      onCurrentStepChange,
      getStepName,
      onPublish,
      onPriceChanged,
      uploadImageHandler,
    };
  },
});
